/**
 * RightInfoPanel Component
 * Right sidebar with project info, media filters, and layer controls
 * Matches the dark theme of the left sidebar
 */

import { useState, useRef, useCallback } from 'react';
import './RightInfoPanel.css';

export interface LayerItem {
  id: string;
  name: string;
  visible: boolean;
  opacity: number;
  type: 'base' | 'overlay';
}

export interface MediaTypeItem {
  id: string;
  label: string;
  icon: string;
  count: number;
  active: boolean;
}

type ExpandedSection = 'project' | 'media' | 'layers' | 'measurements' | null;

interface RightInfoPanelProps {
  // Project info
  projectName: string;
  address?: string;

  // Media type filters
  mediaTypes?: MediaTypeItem[];
  onMediaTypeToggle?: (id: string) => void;

  // Layer controls
  layers: LayerItem[];
  onLayerToggle: (id: string) => void;
  onLayerOpacityChange: (id: string, opacity: number) => void;

  // Measurements
  measurements?: Array<{
    id: string;
    type: 'distance' | 'area';
    value: number;
  }>;
  onClearMeasurements?: () => void;
  onRemoveMeasurement?: (id: string) => void;

  // Panel controls
  onClose?: () => void;
}

export function RightInfoPanel({
  projectName,
  address,
  mediaTypes = [],
  onMediaTypeToggle,
  layers,
  onLayerToggle,
  onLayerOpacityChange,
  measurements = [],
  onClearMeasurements,
  onRemoveMeasurement,
  onClose,
}: RightInfoPanelProps) {
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>('media');

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const formatMeasurement = (m: { type: 'distance' | 'area'; value: number }): string => {
    if (m.type === 'distance') {
      return m.value >= 1000 ? `${(m.value / 1000).toFixed(2)} km` : `${m.value.toFixed(2)} m`;
    } else {
      if (m.value >= 1000000) return `${(m.value / 1000000).toFixed(4)} km²`;
      if (m.value >= 10000) return `${(m.value / 10000).toFixed(4)} ha`;
      return `${m.value.toFixed(2)} m²`;
    }
  };

  // Get media type icon SVG
  const getMediaIcon = (iconType: string) => {
    switch (iconType) {
      case 'hd-image':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        );
      case 'photo':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        );
      case '360':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
            <path d="M2 12h20" />
          </svg>
        );
      case 'video':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="6" width="15" height="12" rx="2" />
            <path d="M17 10l5-3v10l-5-3v-4z" />
          </svg>
        );
      case '3d':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l9 5v10l-9 5-9-5V7l9-5z" />
            <path d="M12 22V12" />
            <path d="M21 7l-9 5-9-5" />
          </svg>
        );
      case 'vr-tour':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 8a4 4 0 014-4h12a4 4 0 014 4v8a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" />
            <circle cx="8" cy="12" r="2" />
            <circle cx="16" cy="12" r="2" />
          </svg>
        );
      case 'embed':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        );
      case 'poi':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        );
      case 'aoi':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18" />
            <path d="M15 3v18" />
            <path d="M3 9h18" />
            <path d="M3 15h18" />
          </svg>
        );
      case 'assets':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
          </svg>
        );
    }
  };

  // ─── Smooth opacity slider: update parent only on pointerup ────────
  const draggingOpacityRef = useRef<{ id: string; value: number } | null>(null);
  const [localOpacities, setLocalOpacities] = useState<Record<string, number>>({});

  const handleOpacityInput = useCallback((id: string, value: number) => {
    // Update local state immediately for responsive slider UI
    setLocalOpacities(prev => ({ ...prev, [id]: value }));
    // Also update the parent so the Cesium layer updates in real-time
    onLayerOpacityChange(id, value);
    draggingOpacityRef.current = { id, value };
  }, [onLayerOpacityChange]);

  const handleOpacityCommit = useCallback((id: string) => {
    // Clear local override so we go back to using the prop value
    setLocalOpacities(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    draggingOpacityRef.current = null;
  }, []);

  const totalMediaCount = mediaTypes.reduce((sum, m) => sum + m.count, 0);

  return (
    <div className="right-sidebar">
      {/* Header with close button */}
      {onClose && (
        <div className="right-sidebar-header">
          <span className="right-sidebar-title">Info</span>
          <button className="right-sidebar-close" onClick={onClose} title="Close panel">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Project Section */}
      <div className="sidebar-section">
        <button
          className={`sidebar-section-btn ${expandedSection === 'project' ? 'active' : ''}`}
          onClick={() => toggleSection('project')}
        >
          <svg className="sidebar-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <span>Project</span>
        </button>

        {expandedSection === 'project' && (
          <div className="sidebar-section-content">
            <div className="right-sidebar-project">
              <div className="right-sidebar-project-name">{projectName}</div>
              {address && <div className="right-sidebar-project-address">{address}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Media Types Section */}
      {mediaTypes.length > 0 && (
        <div className="sidebar-section">
          <button
            className={`sidebar-section-btn ${expandedSection === 'media' ? 'active' : ''}`}
            onClick={() => toggleSection('media')}
          >
            <svg className="sidebar-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <span>Media</span>
            {totalMediaCount > 0 && (
              <span className="sidebar-section-badge">{totalMediaCount}</span>
            )}
          </button>

          {expandedSection === 'media' && (
            <div className="sidebar-section-content">
              <p className="right-sidebar-media-hint">Toggle media visibility on map</p>
              <div className="right-sidebar-media-grid">
                {mediaTypes.map(media => (
                  <button
                    key={media.id}
                    className={`right-sidebar-media-item ${media.active ? 'active' : ''} ${media.count === 0 ? 'disabled' : ''}`}
                    onClick={() => onMediaTypeToggle?.(media.id)}
                    disabled={media.count === 0}
                    title={`${media.label} (${media.count})`}
                  >
                    <span className={`right-sidebar-media-icon right-sidebar-media-icon--${media.id}`}>
                      {getMediaIcon(media.icon)}
                    </span>
                    <span className="right-sidebar-media-label">{media.label}</span>
                    {media.count > 0 && (
                      <span className="right-sidebar-media-count">{media.count}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Layers Section */}
      <div className="sidebar-section">
        <button
          className={`sidebar-section-btn ${expandedSection === 'layers' ? 'active' : ''}`}
          onClick={() => toggleSection('layers')}
        >
          <svg className="sidebar-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          <span>Layers</span>
          {layers.filter(l => l.visible).length > 0 && (
            <span className="sidebar-section-badge">{layers.filter(l => l.visible).length}</span>
          )}
        </button>

        {expandedSection === 'layers' && (
          <div className="sidebar-section-content">
            {layers.length === 0 ? (
              <p className="sidebar-empty-text">No layers available</p>
            ) : (
              <div className="right-sidebar-layers">
                {layers.map(layer => (
                  <div key={layer.id} className="right-sidebar-layer">
                    <label className="sidebar-checkbox">
                      <input
                        type="checkbox"
                        checked={layer.visible}
                        onChange={() => onLayerToggle(layer.id)}
                      />
                      <span>{layer.name}</span>
                    </label>
                    <input
                      type="range"
                      className="right-sidebar-opacity"
                      min="0"
                      max="1"
                      step="0.01"
                      value={localOpacities[layer.id] ?? layer.opacity}
                      onInput={(e) => handleOpacityInput(layer.id, parseFloat((e.target as HTMLInputElement).value))}
                      onChange={() => {/* handled by onInput */}}
                      onPointerUp={() => handleOpacityCommit(layer.id)}
                      onMouseUp={() => handleOpacityCommit(layer.id)}
                      disabled={!layer.visible}
                      title={`Opacity: ${Math.round((localOpacities[layer.id] ?? layer.opacity) * 100)}%`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Measurements Section */}
      {measurements.length > 0 && (
        <div className="sidebar-section">
          <button
            className={`sidebar-section-btn ${expandedSection === 'measurements' ? 'active' : ''}`}
            onClick={() => toggleSection('measurements')}
          >
            <svg className="sidebar-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 12h20M2 12l4-4M2 12l4 4M22 12l-4-4M22 12l-4 4" />
            </svg>
            <span>Measurements</span>
            <span className="sidebar-section-badge">{measurements.length}</span>
          </button>

          {expandedSection === 'measurements' && (
            <div className="sidebar-section-content">
              <div className="right-sidebar-measurements">
                {measurements.map(m => (
                  <div key={m.id} className="right-sidebar-measurement">
                    <span className="right-sidebar-measurement-icon">
                      {m.type === 'distance' ? '📏' : '📐'}
                    </span>
                    <span className="right-sidebar-measurement-value">
                      {formatMeasurement(m)}
                    </span>
                    {onRemoveMeasurement && (
                      <button
                        className="right-sidebar-measurement-remove"
                        onClick={() => onRemoveMeasurement(m.id)}
                        title="Remove"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {onClearMeasurements && (
                  <button
                    className="right-sidebar-clear-btn"
                    onClick={onClearMeasurements}
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
